import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { RoomMemberDto } from '../../../../types/chat';
import { isImageMime, formatBytes } from '../../../../lib/attachments';
import {
  MEDIA_KINDS,
  buildDayGroups,
  matchesQuery,
  mediaKindOf,
  queryTokens,
  type MediaKind,
  type MediaTab,
} from '../../../../lib/attachment-groups';
import { useChatMediaStore } from '../../../../store/chatMediaStore';
import { tChat, getChatLang, subscribeChatLang, type ChatI18nKey } from '../../../../lib/chat-i18n';
import { ChevronDown, ChevronUp, Search, X } from '../../../../icons';
import { useRoomAttachmentsDated } from './useRoomAttachmentsDated';
import { MediaSortButtons } from './MediaSortButtons';
import { MediaDayGroup } from './MediaDayGroup';
import { MediaFileRowItem } from './MediaFileRowItem';
import { MediaImageTile } from './MediaImageTile';
import { MediaLightbox } from './MediaLightbox';

/**
 * 미디어 브라우저 (chat-media-date-groups — 2026-08-07) — 우측 "대화상대" 사이드바 하단.
 *
 * 기존 `ChatMediaPanel` 은 이미지·파일을 최신순 평면으로만 나열해, 첨부가 쌓이면 과거 파일을 찾을
 * 방법이 스크롤뿐이었다. 이 컴포넌트가 그 자리를 대신하며 다음을 제공한다:
 *
 *   - **일자별 그룹 + 펼치기(아코디언)** — 접힌 일자는 바디를 렌더하지 않는다(이미지 presign 절약).
 *   - **아이콘 정렬**(날짜·이름·크기 × 방향) — 이름·크기는 *같은 날짜 안에서만* 적용해 아코디언 유지.
 *   - **검색** — 파일명 + 보낸사람 이름, 매칭 구간 하이라이트, 검색 중에는 모든 그룹 자동 펼침.
 *   - **파일 종류 칩**(문서/표/압축/기타) — `fileCategory()` SSOT 재사용, 건수 0인 칩은 숨김.
 *   - **이미지 라이트박스** — 파일의 미리보기 패널과 대칭.
 *
 * 백엔드 변경 0 — `GET /chat/rooms/:id/attachments` 가 이미 항목별 `createdAt`/`senderId` 를 준다.
 * 기존 `ChatMediaPanel.tsx` 는 롤백 경로로 그대로 남겨둔다.
 */

const SEARCH_DEBOUNCE_MS = 120;

interface ChatMediaBrowserPanelProps {
  roomId: string;
  members: RoomMemberDto[];
}

export function ChatMediaBrowserPanel({ roomId, members }: ChatMediaBrowserPanelProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);

  const { items } = useRoomAttachmentsDated(roomId);
  const tab = useChatMediaStore((s) => s.tab);
  const sortKey = useChatMediaStore((s) => s.sortKey);
  const sortDir = useChatMediaStore((s) => s.sortDir);
  const kind = useChatMediaStore((s) => s.kind);
  const setTab = useChatMediaStore((s) => s.setTab);
  const toggleSort = useChatMediaStore((s) => s.toggleSort);
  const setKind = useChatMediaStore((s) => s.setKind);

  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [override, setOverride] = useState<Set<string> | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // 방이 바뀌면 검색·펼침·라이트박스를 초기화한다(다른 방 상태 잔존 방지).
  useEffect(() => {
    setQueryInput('');
    setQuery('');
    setOverride(null);
    setLightboxIndex(null);
  }, [roomId]);

  useEffect(() => {
    const id = window.setTimeout(() => setQuery(queryInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [queryInput]);

  const senderNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const mem of members) m[mem.userId] = mem.name;
    return m;
  }, [members]);

  const { images, files } = useMemo(() => {
    const imgs = [];
    const fls = [];
    for (const a of items) {
      if (isImageMime(a.mimeType ?? '')) imgs.push(a);
      else fls.push(a);
    }
    return { images: imgs, files: fls };
  }, [items]);

  const source = tab === 'images' ? images : files;

  const groups = useMemo(
    () =>
      buildDayGroups(source, {
        query,
        sortKey,
        sortDir,
        // 종류 칩은 파일 탭 전용 — 이미지 탭에서는 항상 전체.
        kind: tab === 'images' ? 'all' : kind,
        senderNameById,
      }),
    [source, query, sortKey, sortDir, kind, tab, senderNameById],
  );

  // 종류 칩 건수 — 검색 결과 기준(칩이 "지금 고를 수 있는 것" 을 반영하게).
  const kindCounts = useMemo(() => {
    const counts: Record<MediaKind, number> = { all: 0, doc: 0, sheet: 0, archive: 0, etc: 0 };
    const tokens = queryTokens(query);
    for (const a of files) {
      if (!matchesQuery(a, tokens, senderNameById)) continue;
      counts.all += 1;
      counts[mediaKindOf(a)] += 1;
    }
    return counts;
  }, [files, query, senderNameById]);

  const searching = query.length > 0;

  // 검색 중에는 매칭된 모든 그룹을 펼친다 — 찾으려고 검색했는데 다시 펼치게 하지 않는다.
  const defaultExpanded = useMemo(
    () => new Set(searching ? groups.map((g) => g.dayKey) : groups.slice(0, 1).map((g) => g.dayKey)),
    [searching, groups],
  );

  // 검색어·방·탭·종류가 바뀌면 사용자의 수동 펼침을 초기화(문맥이 달라졌으므로).
  useEffect(() => {
    setOverride(null);
  }, [searching, roomId, tab, kind]);

  const expanded = override ?? defaultExpanded;

  const toggleDay = useCallback(
    (dayKey: string) => {
      setOverride((prev) => {
        const next = new Set(prev ?? defaultExpanded);
        if (next.has(dayKey)) next.delete(dayKey);
        else next.add(dayKey);
        return next;
      });
    },
    [defaultExpanded],
  );

  const allExpanded = groups.length > 0 && groups.every((g) => expanded.has(g.dayKey));
  const toggleAll = useCallback(() => {
    setOverride(allExpanded ? new Set<string>() : new Set(groups.map((g) => g.dayKey)));
  }, [allExpanded, groups]);

  const tokens = useMemo(() => queryTokens(query), [query]);

  // 라이트박스 이동 대상 = 필터·정렬이 적용된 전체 이미지(접힌 일자 포함).
  const flatImages = useMemo(() => (tab === 'images' ? groups.flatMap((g) => g.items) : []), [tab, groups]);
  // 목록이 줄어 인덱스가 범위를 벗어나면 닫는다(SSE 로 목록이 바뀌는 경우 방어).
  useEffect(() => {
    if (lightboxIndex !== null && lightboxIndex >= flatImages.length) setLightboxIndex(null);
  }, [lightboxIndex, flatImages.length]);

  const shownCount = groups.reduce((n, g) => n + g.items.length, 0);
  const shownBytes = groups.reduce((n, g) => n + g.totalBytes, 0);

  const isEmptyAll = items.length === 0;
  const isEmptyTab = !isEmptyAll && source.length === 0;
  const isNoResults = !isEmptyAll && !isEmptyTab && groups.length === 0;

  const ToggleAllIcon = allExpanded ? ChevronUp : ChevronDown;
  const toggleAllLabel = tChat(allExpanded ? 'chat.media.collapse_all' : 'chat.media.expand_all');

  return (
    <div className="chat-media-panel chat-media-browser" aria-label={tChat('chat.media.section_aria')}>
      <div className="chat-media-toolbar">
        <div className="chat-media-tabs" role="tablist" aria-label={tChat('chat.media.tabs_aria')}>
          {(['files', 'images'] as MediaTab[]).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`chat-media-tab-${t}`}
              aria-selected={tab === t}
              aria-controls="chat-media-tabpanel"
              className={`chat-media-tab${tab === t ? ' is-active' : ''}`}
              onClick={() => setTab(t)}
            >
              {tChat(t === 'files' ? 'chat.media.tab_files' : 'chat.media.tab_images')}
              <span className="chat-media-tab-count">{t === 'files' ? files.length : images.length}</span>
            </button>
          ))}
        </div>

        <div className="chat-media-search">
          <Search className="chat-media-search-icon" size={14} aria-hidden />
          <input
            type="search"
            className="chat-media-search-input"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder={tChat(
              tab === 'images' ? 'chat.media.search_placeholder_images' : 'chat.media.search_placeholder_files',
            )}
            aria-label={tChat('chat.media.search_aria')}
          />
          {queryInput && (
            <button
              type="button"
              className="chat-media-search-clear"
              onClick={() => setQueryInput('')}
              aria-label={tChat('chat.media.search_clear')}
              title={tChat('chat.media.search_clear')}
            >
              <X size={12} aria-hidden />
            </button>
          )}
        </div>

        {tab === 'files' && (
          <div className="chat-media-chips scrollbar-thin" role="group" aria-label={tChat('chat.media.kind_aria')}>
            {MEDIA_KINDS.filter((k) => k === 'all' || k === kind || kindCounts[k] > 0).map((k) => (
              <button
                key={k}
                type="button"
                className={`chat-media-chip${kind === k ? ' is-active' : ''}`}
                onClick={() => setKind(k)}
                aria-pressed={kind === k}
              >
                {tChat(`chat.media.kind.${k}` as ChatI18nKey)}
              </button>
            ))}
          </div>
        )}

        <div className="chat-media-controls">
          <MediaSortButtons sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
          <button
            type="button"
            className="chat-media-expand-all"
            onClick={toggleAll}
            disabled={groups.length === 0}
            aria-label={toggleAllLabel}
            title={toggleAllLabel}
          >
            <ToggleAllIcon size={14} aria-hidden />
          </button>
        </div>
      </div>

      {/* 탭 전환의 대상 영역 — tablist 가 가리킬 tabpanel 이 있어야 ARIA 가 성립한다. */}
      <div role="tabpanel" id="chat-media-tabpanel" aria-labelledby={`chat-media-tab-${tab}`}>
      {isEmptyAll && <p className="chat-media-empty">{tChat('chat.media.empty')}</p>}
      {isEmptyTab && (
        <p className="chat-media-empty">
          {tChat(tab === 'images' ? 'chat.media.tab_empty_images' : 'chat.media.tab_empty_files')}
        </p>
      )}
      {isNoResults && (
        <p className="chat-media-empty">
          {tChat('chat.media.no_results')}
          <button type="button" className="chat-media-empty-action" onClick={() => setQueryInput('')}>
            {tChat('chat.media.search_clear')}
          </button>
        </p>
      )}

      {groups.length > 0 && (
        <>
          <p className="chat-media-summary">
            {tChat('chat.media.summary', { count: shownCount, size: formatBytes(shownBytes) })}
            {sortKey !== 'date' && (
              <span className="chat-media-summary-note">{tChat('chat.media.sort_note')}</span>
            )}
          </p>

          {groups.map((g) => (
            <MediaDayGroup
              key={g.dayKey || 'unknown'}
              group={g}
              expanded={expanded.has(g.dayKey)}
              onToggle={toggleDay}
            >
              {tab === 'images' ? (
                <div className="chat-media-grid">
                  {g.items.map((a) => (
                    <MediaImageTile
                      key={a.fileId}
                      attachment={a}
                      onOpen={() => setLightboxIndex(flatImages.findIndex((x) => x.fileId === a.fileId))}
                    />
                  ))}
                </div>
              ) : (
                <ul className="chat-media-file-list">
                  {g.items.map((a) => (
                    <MediaFileRowItem
                      key={a.fileId}
                      attachment={a}
                      roomId={roomId}
                      tokens={tokens}
                      senderName={a.senderId ? (senderNameById[a.senderId] ?? null) : null}
                    />
                  ))}
                </ul>
              )}
            </MediaDayGroup>
          ))}
        </>
      )}
      </div>

      {lightboxIndex !== null && lightboxIndex >= 0 && (
        <MediaLightbox
          images={flatImages}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          roomId={roomId}
        />
      )}
    </div>
  );
}
